import { useState, useCallback } from 'react'
import { Upload, FileText, CheckCircle, AlertCircle, X, Download } from 'lucide-react'
import { PageLayout } from '@/layouts'
import { Button, Card, CardTitle, CardDescription } from '@/components/ui'
import { useImportPrivacy } from '@/hooks/use-privacy'
import { useToast } from '@/hooks/use-toast'
import { ImportPrivacyResponse } from '@/models/privacy'

type ImportStatus = 'idle' | 'uploading' | 'success' | 'error'

interface ParsedCSV {
  headers: string[]
  rows: string[][]
  rawLines: string[]
}

// Parse CSV content into headers and rows
function parseCSV(content: string): ParsedCSV {
  const lines = content.split(/\r?\n/).filter(line => line.trim())
  if (lines.length === 0) return { headers: [], rows: [], rawLines: [] }

  // Simple CSV parser that handles quoted fields
  const parseLine = (line: string): string[] => {
    const result: string[] = []
    let current = ''
    let inQuotes = false

    for (let i = 0; i < line.length; i++) {
      const char = line[i]
      if (char === '"') {
        if (inQuotes && line[i + 1] === '"') {
          current += '"'
          i++
        } else {
          inQuotes = !inQuotes
        }
      } else if (char === ',' && !inQuotes) {
        result.push(current.trim())
        current = ''
      } else {
        current += char
      }
    }
    result.push(current.trim())
    return result
  }

  const headers = parseLine(lines[0])
  const rows = lines.slice(1).map(parseLine)

  return { headers, rows, rawLines: lines }
}

export function PrivacyImportPage() {
  const [file, setFile] = useState<File | null>(null)
  const [dragActive, setDragActive] = useState(false)
  const [status, setStatus] = useState<ImportStatus>('idle')
  const [result, setResult] = useState<ImportPrivacyResponse | null>(null)
  const [parsedCSV, setParsedCSV] = useState<ParsedCSV | null>(null)

  const importMutation = useImportPrivacy()
  const { toast } = useToast()

  const handleDrag = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true)
    } else if (e.type === 'dragleave') {
      setDragActive(false)
    }
  }, [])

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setDragActive(false)

    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      const droppedFile = e.dataTransfer.files[0]
      if (droppedFile.type === 'text/csv' || droppedFile.name.endsWith('.csv')) {
        setFile(droppedFile)
        setStatus('idle')
        setResult(null)
        // Parse CSV content
        droppedFile.text().then(content => {
          setParsedCSV(parseCSV(content))
        })
      } else {
        toast({
          title: 'Invalid file type',
          description: 'Please upload a CSV file',
          variant: 'error',
        })
      }
    }
  }, [toast])

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const selectedFile = e.target.files[0]
      setFile(selectedFile)
      setStatus('idle')
      setResult(null)
      // Parse CSV content
      selectedFile.text().then(content => {
        setParsedCSV(parseCSV(content))
      })
    }
  }

  const handleUpload = async () => {
    if (!file) return

    setStatus('uploading')
    try {
      const response = await importMutation.mutateAsync(file)
      setResult(response)
      setStatus('success')
      toast({
        title: 'Import Complete',
        description: `Created: ${response.created}, Updated: ${response.updated}, Failed: ${response.failed}`,
        variant: response.failed > 0 ? 'warning' : 'success',
      })
    } catch (error) {
      setStatus('error')
      toast({
        title: 'Import Failed',
        description: error instanceof Error ? error.message : 'Failed to import CSV file',
        variant: 'error',
      })
    }
  }

  const handleClear = () => {
    setFile(null)
    setStatus('idle')
    setResult(null)
    setParsedCSV(null)
  }

  const downloadTemplate = () => {
    const headers = [
      'email',
      'phone',
      'first_name',
      'last_name',
      'opt_out_sale',
      'opt_out_share',
      'remove_data',
      'do_not_contact',
      'jurisdiction',
      'received_at',
      'source',
      'notes',
    ]
    const exampleRow = [
      'user@example.com',
      '+1-555-123-4567',
      'John',
      'Doe',
      'true',
      'false',
      'true',
      'false',
      'ccpa',
      '2024-01-15',
      'Web form',
      'Example privacy request',
    ]

    const csvContent = [headers.join(','), exampleRow.join(',')].join('\n')
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
    const link = document.createElement('a')
    link.href = URL.createObjectURL(blob)
    link.download = 'privacy-import-template.csv'
    link.click()
  }

  // Helper to escape CSV values
  const escapeCSV = (value: string): string => {
    if (value.includes(',') || value.includes('"') || value.includes('\n')) {
      return `"${value.replace(/"/g, '""')}"`
    }
    return value
  }

  // Download error summary as CSV (row, contact, error message)
  const downloadErrorList = () => {
    if (!result?.errors || result.errors.length === 0) return

    const headers = ['Row', 'Contact', 'Error']
    const rows = result.errors.map(error => {
      const contact = [error.email, error.phone].filter(Boolean).join(' / ') || '-'
      return [
        error.row.toString(),
        escapeCSV(contact),
        escapeCSV(error.message),
      ].join(',')
    })

    const csvContent = [headers.join(','), ...rows].join('\n')
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
    const link = document.createElement('a')
    link.href = URL.createObjectURL(blob)
    link.download = 'import-errors.csv'
    link.click()
  }

  // Download original error rows as CSV (with headers)
  const downloadErrorRows = () => {
    if (!result?.errors || result.errors.length === 0 || !parsedCSV) return

    // Get the row indices that had errors (row numbers are 1-indexed, rows array is 0-indexed)
    // Row 1 = header, Row 2 = first data row (index 0 in rows array)
    const errorRowIndices = result.errors.map(error => error.row - 2)
    const errorRows = errorRowIndices
      .filter(index => index >= 0 && index < parsedCSV.rows.length)
      .map(index => parsedCSV.rows[index].map(escapeCSV).join(','))

    const csvContent = [
      parsedCSV.headers.map(escapeCSV).join(','),
      ...errorRows,
    ].join('\n')

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
    const link = document.createElement('a')
    link.href = URL.createObjectURL(blob)
    link.download = 'import-error-rows.csv'
    link.click()
  }

  return (
    <PageLayout
      title="Import CSV"
      description="Bulk import privacy subjects from CSV file"
      actions={
        <Button variant="secondary" onClick={downloadTemplate}>
          <Download className="h-4 w-4 mr-2" />
          Download Template
        </Button>
      }
    >
      <div className="max-w-2xl">
        {/* Upload Area */}
        <Card>
          <CardTitle>Upload CSV File</CardTitle>
          <CardDescription>
            Import privacy subjects from a CSV file. Existing records will be updated based on email address.
          </CardDescription>

          <div
            className={`
              mt-6 border-2 border-dashed rounded-lg p-8 text-center transition-colors
              ${dragActive ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20' : 'border-slate-300 dark:border-slate-600'}
              ${file ? 'bg-slate-50 dark:bg-slate-800' : ''}
            `}
            onDragEnter={handleDrag}
            onDragLeave={handleDrag}
            onDragOver={handleDrag}
            onDrop={handleDrop}
          >
            {!file ? (
              <>
                <Upload className="mx-auto h-12 w-12 text-slate-400" />
                <div className="mt-4">
                  <label htmlFor="file-upload" className="cursor-pointer">
                    <span className="text-primary-600 hover:text-primary-700 font-medium">
                      Upload a file
                    </span>
                    <input
                      id="file-upload"
                      name="file-upload"
                      type="file"
                      accept=".csv"
                      className="sr-only"
                      onChange={handleFileChange}
                    />
                  </label>
                  <span className="text-slate-500 dark:text-slate-400"> or drag and drop</span>
                </div>
                <p className="mt-2 text-xs text-slate-500 dark:text-slate-400">CSV files only</p>
              </>
            ) : (
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <FileText className="h-10 w-10 text-primary-500" />
                  <div className="text-left">
                    <p className="font-medium text-slate-900 dark:text-white">{file.name}</p>
                    <p className="text-sm text-slate-500 dark:text-slate-400">
                      {(file.size / 1024).toFixed(1)} KB
                    </p>
                  </div>
                </div>
                <button
                  onClick={handleClear}
                  className="p-2 text-slate-400 hover:text-slate-600 dark:hover:text-slate-300"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
            )}
          </div>

          {/* Upload Button */}
          <div className="mt-6 flex justify-end">
            <Button
              variant="primary"
              onClick={handleUpload}
              disabled={!file || status === 'uploading'}
              loading={status === 'uploading'}
            >
              {status === 'uploading' ? 'Importing...' : 'Import CSV'}
            </Button>
          </div>
        </Card>

        {/* Results */}
        {result && (
          <Card className="mt-6">
            <div className="flex items-start gap-4">
              {status === 'success' && result.failed === 0 ? (
                <CheckCircle className="h-8 w-8 text-green-500 flex-shrink-0" />
              ) : (
                <AlertCircle className="h-8 w-8 text-yellow-500 flex-shrink-0" />
              )}
              <div className="flex-1">
                <h3 className="font-semibold text-slate-900 dark:text-white">Import Results</h3>
                <div className="mt-3 grid grid-cols-3 gap-4 text-center">
                  <div className="p-3 bg-green-50 dark:bg-green-900/20 rounded-lg">
                    <p className="text-2xl font-bold text-green-600">{result.created}</p>
                    <p className="text-xs text-green-700 dark:text-green-400">Created</p>
                  </div>
                  <div className="p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
                    <p className="text-2xl font-bold text-blue-600">{result.updated}</p>
                    <p className="text-xs text-blue-700 dark:text-blue-400">Updated</p>
                  </div>
                  <div className="p-3 bg-red-50 dark:bg-red-900/20 rounded-lg">
                    <p className="text-2xl font-bold text-red-600">{result.failed}</p>
                    <p className="text-xs text-red-700 dark:text-red-400">Failed</p>
                  </div>
                </div>

                {/* Errors */}
                {result.errors && result.errors.length > 0 && (
                  <div className="mt-4">
                    <div className="flex items-center justify-between mb-2">
                      <h4 className="text-sm font-medium text-slate-900 dark:text-white">
                        Errors ({result.errors.length})
                      </h4>
                      <div className="flex gap-2">
                        <Button variant="secondary" size="sm" onClick={downloadErrorList}>
                          <Download className="h-3 w-3 mr-1" />
                          Error List
                        </Button>
                        {parsedCSV && (
                          <Button variant="secondary" size="sm" onClick={downloadErrorRows}>
                            <Download className="h-3 w-3 mr-1" />
                            Error Rows
                          </Button>
                        )}
                      </div>
                    </div>
                    <div className="max-h-48 overflow-y-auto">
                      <table className="w-full text-sm">
                        <thead className="text-xs text-slate-500 uppercase bg-slate-50 dark:bg-slate-800">
                          <tr>
                            <th className="px-2 py-1 text-left">Row</th>
                            <th className="px-2 py-1 text-left">Contact</th>
                            <th className="px-2 py-1 text-left">Error</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-200 dark:divide-slate-700">
                          {result.errors.map((error, index) => (
                            <tr key={index}>
                              <td className="px-2 py-1 text-slate-900 dark:text-white">{error.row}</td>
                              <td className="px-2 py-1 text-slate-600 dark:text-slate-400">
                                {[error.email, error.phone].filter(Boolean).join(' / ') || '-'}
                              </td>
                              <td className="px-2 py-1 text-red-600 dark:text-red-400">
                                {error.message}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </Card>
        )}

        {/* Instructions */}
        <Card className="mt-6">
          <CardTitle>CSV Format</CardTitle>
          <CardDescription>Your CSV file should include the following columns:</CardDescription>

          <div className="mt-4 overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-xs text-slate-500 uppercase bg-slate-50 dark:bg-slate-800">
                <tr>
                  <th className="px-3 py-2 text-left">Column</th>
                  <th className="px-3 py-2 text-left">Required</th>
                  <th className="px-3 py-2 text-left">Description</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-200 dark:divide-slate-700">
                <tr>
                  <td className="px-3 py-2 font-mono text-xs">email</td>
                  <td className="px-3 py-2 text-green-600">Yes</td>
                  <td className="px-3 py-2 text-slate-600 dark:text-slate-400">Email address (used as unique identifier)</td>
                </tr>
                <tr>
                  <td className="px-3 py-2 font-mono text-xs">phone</td>
                  <td className="px-3 py-2 text-slate-400">No</td>
                  <td className="px-3 py-2 text-slate-600 dark:text-slate-400">Phone number</td>
                </tr>
                <tr>
                  <td className="px-3 py-2 font-mono text-xs">first_name</td>
                  <td className="px-3 py-2 text-slate-400">No</td>
                  <td className="px-3 py-2 text-slate-600 dark:text-slate-400">First name</td>
                </tr>
                <tr>
                  <td className="px-3 py-2 font-mono text-xs">last_name</td>
                  <td className="px-3 py-2 text-slate-400">No</td>
                  <td className="px-3 py-2 text-slate-600 dark:text-slate-400">Last name</td>
                </tr>
                <tr>
                  <td className="px-3 py-2 font-mono text-xs">opt_out_sale</td>
                  <td className="px-3 py-2 text-green-600">Yes</td>
                  <td className="px-3 py-2 text-slate-600 dark:text-slate-400">true/false</td>
                </tr>
                <tr>
                  <td className="px-3 py-2 font-mono text-xs">opt_out_share</td>
                  <td className="px-3 py-2 text-green-600">Yes</td>
                  <td className="px-3 py-2 text-slate-600 dark:text-slate-400">true/false</td>
                </tr>
                <tr>
                  <td className="px-3 py-2 font-mono text-xs">remove_data</td>
                  <td className="px-3 py-2 text-green-600">Yes</td>
                  <td className="px-3 py-2 text-slate-600 dark:text-slate-400">true/false</td>
                </tr>
                <tr>
                  <td className="px-3 py-2 font-mono text-xs">do_not_contact</td>
                  <td className="px-3 py-2 text-green-600">Yes</td>
                  <td className="px-3 py-2 text-slate-600 dark:text-slate-400">true/false</td>
                </tr>
                <tr>
                  <td className="px-3 py-2 font-mono text-xs">jurisdiction</td>
                  <td className="px-3 py-2 text-green-600">Yes</td>
                  <td className="px-3 py-2 text-slate-600 dark:text-slate-400">ccpa, gdpr, or other</td>
                </tr>
                <tr>
                  <td className="px-3 py-2 font-mono text-xs">received_at</td>
                  <td className="px-3 py-2 text-slate-400">No</td>
                  <td className="px-3 py-2 text-slate-600 dark:text-slate-400">Date received (YYYY-MM-DD)</td>
                </tr>
                <tr>
                  <td className="px-3 py-2 font-mono text-xs">source</td>
                  <td className="px-3 py-2 text-slate-400">No</td>
                  <td className="px-3 py-2 text-slate-600 dark:text-slate-400">Source of request</td>
                </tr>
                <tr>
                  <td className="px-3 py-2 font-mono text-xs">notes</td>
                  <td className="px-3 py-2 text-slate-400">No</td>
                  <td className="px-3 py-2 text-slate-600 dark:text-slate-400">Additional notes</td>
                </tr>
              </tbody>
            </table>
          </div>
        </Card>
      </div>
    </PageLayout>
  )
}
